import React, { useEffect, useState } from "react";
import axios from "axios";
import { Plus } from "lucide-react";
import { useCart, useLang } from "../store";
import { useT } from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Resolve image URLs: absolute (https://...) stay as-is; relative (/cups/...) served from frontend public.
const resolveImg = (path) => {
  if (!path) return "";
  if (path.startsWith("http")) return path;
  return path; // served by CRA from /public
};

// soft tinted backgrounds per product
const TINT = {
  "velora-pink": "#FFE0EC",
  "velora-blue": "#D6F1FF",
  "velora-yellow": "#FFF6CC",
  "velora-green": "#E9FAC8",
  "velora-purple": "#EDE3FF",
  "velora-orange": "#FFE3CF",
};

export default function ProductShowcase() {
  const { lang } = useLang();
  const t = useT(lang);
  const { addToCart } = useCart();
  const [products, setProducts] = useState([]);
  const [active, setActive] = useState(0);

  useEffect(() => {
    axios.get(`${API}/products`).then((r) => {
      const all = r.data.products || [];
      // Only show slushy cups (exclude pet & sneaker categories)
      setProducts(all.filter((p) => !p.category));
    });
  }, []);

  if (!products.length) return null;
  const p = products[active];
  const tint = TINT[p.id] || "#FFE0EC";

  return (
    <section id="shop" className="border-b-[3px] border-[#0C0A09]" style={{ background: tint, transition: "background 400ms ease" }}>
      <div className="max-w-7xl mx-auto px-5 md:px-10 py-20 md:py-28">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-12">
          <div>
            <span className="nb-badge mb-4">{t.shop.eyebrow}</span>
            <h2 className="font-display text-5xl md:text-7xl leading-none tracking-tight mt-3">{t.shop.title}</h2>
          </div>
          <p className="max-w-md text-lg font-medium">{t.shop.sub}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          {/* Big product photo */}
          <div className="lg:col-span-6 flex items-center justify-center relative min-h-[440px]">
            <div className="absolute w-80 h-80 rounded-full bg-white border-[3px] border-[#0C0A09]" />
            <div className="relative float-cup">
              <img
                key={p.id}
                src={resolveImg(p.image)}
                alt={lang === "es" ? p.name_es : p.name_en}
                data-testid="active-product-image"
                className="w-[340px] md:w-[400px] h-auto drop-shadow-[8px_10px_0_#0C0A09]"
              />
            </div>
          </div>

          {/* Right panel */}
          <div className="lg:col-span-6">
            <div className="bg-white border-[3px] border-[#0C0A09] shadow-[8px_8px_0_0_#0C0A09] p-7 md:p-9">
              <div className="flex items-center gap-3 mb-2">
                <span className="w-5 h-5 rounded-full border-2 border-[#0C0A09]" style={{ background: p.hex }} />
                <span className="text-xs font-bold uppercase tracking-[0.2em] opacity-70">{t.shop.flavor}</span>
              </div>
              <h3 data-testid="active-product-name" className="font-display text-5xl md:text-6xl leading-none tracking-tight">
                {lang === "es" ? p.name_es : p.name_en}
              </h3>
              <p className="mt-4 text-lg italic opacity-80">&ldquo;{lang === "es" ? p.personality_es : p.personality_en}&rdquo;</p>

              <div className="mt-6 flex items-baseline gap-3">
                <span className="font-display text-5xl">€{p.price.toFixed(2)}</span>
                {p.original_price && p.original_price > p.price && (
                  <>
                    <span className="font-display text-2xl line-through opacity-50">€{p.original_price.toFixed(0)}</span>
                    <span className="px-2 py-1 bg-[#FF3D7F] text-white border-[2px] border-[#0C0A09] rounded-full text-[10px] font-bold uppercase tracking-widest">
                      {lang === "es" ? "-60%" : "-60%"}
                    </span>
                  </>
                )}
              </div>

              {/* Scarcity / urgency indicator */}
              <div data-testid="scarcity-indicator" className="mt-4 flex items-center gap-2 bg-[#FF3D7F]/10 border-[3px] border-[#FF3D7F] px-3 py-2">
                <div className="w-2 h-2 rounded-full bg-[#FF3D7F] animate-pulse" />
                <div className="text-xs font-bold uppercase tracking-[0.18em] text-[#FF3D7F]">
                  {lang === "es" ? "⚡ ¡Quedan pocas unidades!" : "⚡ Few units left!"}
                </div>
              </div>

              <button
                data-testid={`add-to-cart-${p.slug}`}
                onClick={() => addToCart(p)}
                className="nb-btn mt-6 w-full justify-center"
              >
                <Plus size={18} strokeWidth={3} /> {t.shop.addToCart}
              </button>

              {/* Trust signals row */}
              <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                <div className="text-[10px] font-bold uppercase tracking-[0.15em] py-2 border-[2px] border-[#0C0A09] bg-[#FAF9F6]">
                  {lang === "es" ? "📦 Envío 24h" : "📦 24h Ship"}
                </div>
                <div className="text-[10px] font-bold uppercase tracking-[0.15em] py-2 border-[2px] border-[#0C0A09] bg-[#FAF9F6]">
                  {lang === "es" ? "🔒 Pago seguro" : "🔒 Secure pay"}
                </div>
                <div className="text-[10px] font-bold uppercase tracking-[0.15em] py-2 border-[2px] border-[#0C0A09] bg-[#FAF9F6]">
                  {lang === "es" ? "↩️ Devolución 30d" : "↩️ 30d Return"}
                </div>
              </div>

              {/* Color swatches */}
              <div className="mt-8">
                <div className="text-xs font-bold uppercase tracking-[0.2em] opacity-70 mb-3">Colors</div>
                <div className="flex flex-wrap gap-3">
                  {products.map((prod, i) => (
                    <button
                      key={prod.id}
                      data-testid={`color-selector-${prod.slug}`}
                      onClick={() => setActive(i)}
                      aria-label={prod.name_en}
                      className={`relative w-12 h-12 rounded-full border-[3px] border-[#0C0A09] transition-transform ${i === active ? "scale-110 shadow-[3px_3px_0_0_#0C0A09]" : "hover:scale-105"}`}
                      style={{ background: prod.hex }}
                    >
                      {i === active && (
                        <span className="absolute inset-0 rounded-full border-[3px] border-[#0C0A09] pulse-ring" />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
